import { Connect, RequestParams } from '@genesislcap/foundation-comms';
import {
  formatDate,
  formatNumeric,
  formatTime,
  resolveBidTimeHoursRangeForDownload,
  rowMatchesBidTimeHoursRange,
} from '../../../../mbm/utils/bond-bid-utils';
import {
  buildReqRepExportDetails,
  sanitizeCriteriaString,
} from '../../../../mbm/utils/bond-bid-criteria-format-utils';
import {
  showBulkToast,
  escapeCsvField,
  triggerCsvDownload,
  BOND_BIDDING_TIME_FIELDS,
  BOND_BIDDING_DATE_FIELDS,
  BOND_BIDDING_NUMERIC_FIELDS,
  BOND_BIDDING_PRESERVE_AS_TEXT_FIELDS,
} from './bond-bidding-bulk-utils';
import {
  BOND_BIDDING_MESSAGES,
  BOND_BIDDING_TOAST_TITLE,
} from './bond-bidding-messages';

export interface ExportColumn {
  field: string;
  headerName: string;
}

const DATE_TIME_LITERAL_REGEX = /(\d{8}(?:-\d{2}:\d{2}(?::\d{2})?)?)/;
const EQUALITY_REGEX = /^(\w+)\s*==\s*["']([^"']*)["']$/;
const INEQUALITY_REGEX = /^(\w+)\s*!=\s*["']([^"']*)["']$/;
const NULL_CHECK_REGEX = /^(\w+)\s*==\s*null$/;
const NOT_NULL_CHECK_REGEX = /^(\w+)\s*!=\s*null$/;
const DATE_GREATER_EQUAL_REGEX = /Expr\.date(?:Time)?IsGreaterEqual\s*\(\s*(\w+)\s*,\s*["']([^"']+)["']\s*\)/;
const DATE_LESS_EQUAL_REGEX = /Expr\.date(?:Time)?IsLessEqual\s*\(\s*(\w+)\s*,\s*["']([^"']+)["']\s*\)/;
const CONTAINS_IGNORE_CASE_REGEX = /Expr\.containsIgnoreCase\s*\(\s*(\w+)\s*,\s*["']([^"']+)["']\s*\)/;
const STARTS_WITH_REGEX = /^(\w+)\.startsWith\(\s*["']([^"']*)["']\s*\)$/;
const NUMERIC_GE_REGEX = /^(\w+)\s*>=\s*(-?\d+(?:\.\d+)?)$/;
const NUMERIC_LE_REGEX = /^(\w+)\s*<=\s*(-?\d+(?:\.\d+)?)$/;
const NUMERIC_GT_REGEX = /^(\w+)\s*>\s*(-?\d+(?:\.\d+)?)$/;
const NUMERIC_LT_REGEX = /^(\w+)\s*<\s*(-?\d+(?:\.\d+)?)$/;
/** Matches if a literal contains wildcard chars for equality (e.g. CUSIP == "CUSI*"). */
const WILDCARD_CHARS_REGEX = /[\*?]/;
/** Escapes regex specials when building a wildcard pattern from a criteria literal. */
const REGEX_SPECIALS_ESCAPE = /[-/\\^$+?.()|[\]{}]/g;

// Strip only the outermost matching parentheses so nested ones (e.g. inside Expr.containsIgnoreCase(...)) are preserved.
function stripOuterParens(expression: string): string {
  const trimmedExpression = expression.trim();
  if (trimmedExpression.length < 2 || trimmedExpression[0] !== '(') return trimmedExpression;
  let depth = 0;
  for (let index = 0; index < trimmedExpression.length; index++) {
    if (trimmedExpression[index] === '(') depth++;
    else if (trimmedExpression[index] === ')') {
      depth--;
      if (depth === 0) {
        return index === trimmedExpression.length - 1
          ? trimmedExpression.slice(1, -1).trim()
          : trimmedExpression;
      }
    }
  }
  return trimmedExpression;
}

// Split by operator (&& or ||) only when at paren depth 0; ignores parens inside quoted strings.
function splitAtDepthZero(expression: string, operatorPattern: RegExp): string[] {
  const parts: string[] = [];
  let depth = 0;
  let inQuote: string | null = null;
  let segmentStartIndex = 0;
  let index = 0;
  while (index < expression.length) {
    const currentChar = expression[index];
    if (inQuote) {
      if (currentChar === '\\' && index + 1 < expression.length) index++;
      else if (currentChar === inQuote) inQuote = null;
      index++;
      continue;
    }
    if (currentChar === '"' || currentChar === "'") {
      inQuote = currentChar;
      index++;
      continue;
    }
    if (currentChar === '(') depth++;
    else if (currentChar === ')') depth--;
    if (depth === 0) {
      const remainingExpression = expression.slice(index);
      const match = remainingExpression.match(operatorPattern);
      if (match && match.index === 0) {
        parts.push(expression.slice(segmentStartIndex, index).trim());
        segmentStartIndex = index + match[0].length;
        index += match[0].length;
        continue;
      }
    }
    index++;
  }
  parts.push(expression.slice(segmentStartIndex).trim());
  return parts;
}

// Normalise row value and criteria to YYYYMMDD[-HH:mm[:ss]]. Time without seconds (e.g. 10:16) is treated as :00 so download matches grid (e.g. 10:16:38 excluded when end is 10:16).
function toDateKey(value: unknown, literal: string): { rowKey: string; critKey: string } | null {
  // Strip any annotation text; allow optional seconds: yyyyMMdd, yyyyMMdd-HH:mm, or yyyyMMdd-HH:mm:ss.
  const cleaned = (literal.match(DATE_TIME_LITERAL_REGEX) ?? [])[1] ?? literal;
  const withTime = cleaned.length > 8;
  const criteriaHasSeconds = cleaned.length >= 17; // YYYYMMDD-HH:mm:ss

  let rowKey: string;
  if (typeof value === 'number') {
    const iso = new Date(value).toISOString();
    const datePart = iso.slice(0, 10).replace(/-/g, '');
    if (!withTime) {
      rowKey = datePart;
    } else {
      // Use full HH:mm:ss for row so that 10:16:38 is not treated as 10:16 when criteria is "10:16" (end of range).
      rowKey = datePart + '-' + iso.slice(11, 19);
    }
  } else if (value != null) {
    const s = String(value);
    if (!withTime) {
      rowKey = s.slice(0, 8);
    } else {
      const timePart = s.length >= 17 ? s.slice(9, 17) : s.slice(9, 14) + ':00';
      rowKey = s.slice(0, 8) + '-' + timePart;
    }
  } else {
    return null;
  }

  // When criteria has no seconds (e.g. "10:16"), treat as start of minute so "10:16" means 10:16:00.
  const critKey = withTime
    ? criteriaHasSeconds
      ? cleaned.slice(0, 8) + '-' + cleaned.slice(9, 17)
      : cleaned.slice(0, 8) + '-' + cleaned.slice(9, 14) + ':00'
    : cleaned.slice(0, 8);
  return { rowKey, critKey };
}

// Returns true if the row matches the criteria string (e.g. FIELD == "x", Expr.containsIgnoreCase(FIELD, "y"), &&, ||).
export function rowMatchesCriteria(row: Record<string, unknown>, criteria: string): boolean {
  const trimmedCriteria = (criteria || '').trim();
  if (!trimmedCriteria) return true;

  const evaluateSingleExpression = (expression: string): boolean => {
    const normalisedExpression = stripOuterParens(expression);

    let match: RegExpMatchArray | null;

    if ((match = normalisedExpression.match(STARTS_WITH_REGEX))) {
      return String(row[match[1]] ?? '').startsWith(match[2]);
    }
    
    if ((match = normalisedExpression.match(EQUALITY_REGEX))) {
      const value = String(row[match[1]] ?? '');
      const literal = match[2];
      if (!literal.match(WILDCARD_CHARS_REGEX)) return value === literal;
      const escaped = literal.replace(REGEX_SPECIALS_ESCAPE, '\\$&');
      const pattern = '^' + escaped.replace(/\\\*/g, '.*').replace(/\\\?/g, '.') + '$';
      return new RegExp(pattern, 'i').test(value);
    }

    if ((match = normalisedExpression.match(INEQUALITY_REGEX)))
      return String(row[match[1]] ?? '') !== match[2];

    if ((match = normalisedExpression.match(NULL_CHECK_REGEX)))
      return row[match[1]] == null || row[match[1]] === '';

    if ((match = normalisedExpression.match(NOT_NULL_CHECK_REGEX)))
      return row[match[1]] != null && row[match[1]] !== '';

    if ((match = normalisedExpression.match(DATE_GREATER_EQUAL_REGEX))) {
      const keys = toDateKey(row[match[1]], match[2]);
      return keys != null && keys.rowKey >= keys.critKey;
    }

    if ((match = normalisedExpression.match(DATE_LESS_EQUAL_REGEX))) {
      const keys = toDateKey(row[match[1]], match[2]);
      return keys != null && keys.rowKey <= keys.critKey;
    }

    if ((match = normalisedExpression.match(CONTAINS_IGNORE_CASE_REGEX)))
      return String(row[match[1]] ?? '').toLowerCase().includes(match[2].toLowerCase());

    if ((match = normalisedExpression.match(NUMERIC_GE_REGEX)))
      return (row[match[1]] != null && row[match[1]] !== '') && Number(row[match[1]]) >= Number(match[2]);
    if ((match = normalisedExpression.match(NUMERIC_LE_REGEX)))
      return (row[match[1]] != null && row[match[1]] !== '') && Number(row[match[1]]) <= Number(match[2]);
    if ((match = normalisedExpression.match(NUMERIC_GT_REGEX)))
      return (row[match[1]] != null && row[match[1]] !== '') && Number(row[match[1]]) > Number(match[2]);
    if ((match = normalisedExpression.match(NUMERIC_LT_REGEX)))
      return (row[match[1]] != null && row[match[1]] !== '') && Number(row[match[1]]) < Number(match[2]);

    return false;
  };

  const evaluateExpression = (expression: string): boolean => {
    let normalisedExpression = expression.trim();
    while (normalisedExpression.startsWith('(')) {
      const next = stripOuterParens(normalisedExpression);
      if (next === normalisedExpression) break;
      normalisedExpression = next;
    }
    const andParts = splitAtDepthZero(normalisedExpression, /\s*&&\s*/).filter((part) => part.length > 0);
    if (andParts.length > 1) return andParts.every((part) => evaluateExpression(part));
    const orParts = splitAtDepthZero(normalisedExpression, /\s*\|\|\s*/).filter((part) => part.length > 0);
    if (orParts.length > 1) return orParts.some((part) => evaluateExpression(part));
    return evaluateSingleExpression(normalisedExpression);
  };

  return evaluateExpression(trimmedCriteria);
}

export type FetchExportResult =
  | { ok: true; rows: Record<string, unknown>[] }
  | { ok: false; reason: 'network' | 'nack'; message?: string };

export interface FetchMergedExportRowsParams {
  connect: Connect;
  filterValue: string;
  bidModeCriteriaValue: string;
  bidTimeHoursMin: number | null;
  bidTimeHoursMax: number | null;
}

/** Returns true when a Genesis request/reply response indicates failure. */
export function isRequestReplyFailure(response: unknown): boolean {
  if (response == null || typeof response !== 'object') {
    return true;
  }
  const record = response as Record<string, unknown>;
  const messageType = record.MESSAGE_TYPE;
  if (messageType === 'ERROR' || messageType === 'MSG_NACK') {
    return true;
  }
  const reply = record.REPLY;
  if (typeof reply === 'string' && reply.includes('MSG_NACK')) {
    return true;
  }
  if (reply === undefined || reply === null) {
    return true;
  }
  return !Array.isArray(reply);
}

function getRequestReplyErrorMessage(response: unknown): string | undefined {
  if (response == null || typeof response !== 'object') {
    return undefined;
  }
  const errors = (response as { ERROR?: Array<{ TEXT?: string }> }).ERROR;
  if (!Array.isArray(errors)) {
    return undefined;
  }
  const text = errors
    .map((row) => row?.TEXT)
    .filter((value): value is string => typeof value === 'string' && value.length > 0)
    .join('; ');
  return text || undefined;
}

/**
 * Fetches bond + bid export data via ReqRep, merges, and applies the same
 * client-side filters as live download / upload validation.
 */
function buildExportRequestParams(filterValue: string): RequestParams {
  const details = {
    MAX_ROWS: 100000,
    ...buildReqRepExportDetails(filterValue),
  };
  return { REQUEST: {}, DETAILS: details };
}

export async function fetchMergedExportRows(
  params: FetchMergedExportRowsParams
): Promise<FetchExportResult> {
  try {
    const requestParams = buildExportRequestParams(params.filterValue || '');

    const [bondsResponse, bidsResponse] = await Promise.all([
      params.connect.request('ACTIVE_MUNICIPAL_BONDS_EXPORT', requestParams),
      params.connect.request('CPTY_BOND_ACTIVITY_EXPORT', requestParams),
    ]);

    if (isRequestReplyFailure(bondsResponse) || isRequestReplyFailure(bidsResponse)) {
      const message =
        getRequestReplyErrorMessage(bondsResponse) ??
        getRequestReplyErrorMessage(bidsResponse) ??
        BOND_BIDDING_MESSAGES.download.exportRejected;
      return { ok: false, reason: 'nack', message };
    }

    let bonds = (bondsResponse?.REPLY as Record<string, unknown>[]) || [];
    const bids = (bidsResponse?.REPLY as Record<string, unknown>[]) || [];

    // Reference# (BOND_ID) is applied client-side only; all other filters go to ReqRep.
    const clientFilterRaw = sanitizeCriteriaString((params.filterValue || '').trim());
    const { minHours: bidTimeHoursMinForDownload, maxHours: bidTimeHoursMaxForDownload } =
      resolveBidTimeHoursRangeForDownload(
        clientFilterRaw,
        params.bidTimeHoursMin,
        params.bidTimeHoursMax
      );
    if (clientFilterRaw) {
      bonds = bonds.filter((bond) => rowMatchesCriteria(bond, clientFilterRaw));
    }

    const bidMap = new Map<string, Record<string, unknown>>();
    for (const bid of bids) {
      if (bid?.BOND_ID) {
        bidMap.set(bid.BOND_ID as string, bid);
      }
    }

    let mergedRows = bonds.map((bond) => {
      const bid = bidMap.get(bond.BOND_ID as string) || {};
      return { ...bond, ...bid };
    });

    if (params.bidModeCriteriaValue === '((BID_PRICE == null))') {
      mergedRows = mergedRows.filter((row) => row.BID_ID == null || row.BID_ID === '');
    } else if (params.bidModeCriteriaValue === '((BID_PRICE != null))') {
      mergedRows = mergedRows.filter((row) => row.BID_ID != null && row.BID_ID !== '');
    }
    // Firm-for hours: normally in server criteria; keep as safety net if export rows lack FIRM_FOR.
    if (bidTimeHoursMinForDownload != null || bidTimeHoursMaxForDownload != null) {
      const exportHasBidTimeField = mergedRows.some(
        (row) => row != null && Object.prototype.hasOwnProperty.call(row, 'FIRM_FOR')
      );
      if (!exportHasBidTimeField) {
        mergedRows = mergedRows.filter((row) =>
          rowMatchesBidTimeHoursRange(
            row.FIRM_TIME,
            row.DUE_TIME,
            bidTimeHoursMinForDownload,
            bidTimeHoursMaxForDownload
          )
        );
      }
    }

    return { ok: true, rows: mergedRows };
  } catch {
    return { ok: false, reason: 'network' };
  }
}

function formatCellForExport(
  value: unknown,
  field: string,
  data: Record<string, unknown>,
  timeFields: string[],
  dateFields: string[],
  numericFields: string[],
  preserveAsTextFields: Set<string>
): string {
  if (field === 'BID_PRICE_TYPE' && data?.BOND_PRICE_TYPE && data.BOND_PRICE_TYPE !== 'None') {
    return String(data.BOND_PRICE_TYPE);
  }
  if (value == null || value === '') return '';

  if (timeFields.includes(field)) return formatTime(value);
  if (dateFields.includes(field)) return formatDate(value);
  if (numericFields.includes(field)) return formatNumeric((data?.[field] ?? value) as number, 3, true);
  if (preserveAsTextFields.has(field)) return '\t' + String(value);
  return String(value);
}

function buildCsv(
  rows: Record<string, unknown>[],
  columns: Array<{ field: string; headerName: string }>,
  timeFields: string[],
  dateFields: string[],
  numericFields: string[],
  preserveAsTextFields: Set<string>
): string {
  const headerRow = columns.map((column) => escapeCsvField(column.headerName)).join(',');
  const dataRows = rows.map((row) =>
    columns
      .map((column) =>
        escapeCsvField(
          String(
            formatCellForExport(
              row[column.field],
              column.field,
              row,
              timeFields,
              dateFields,
              numericFields,
              preserveAsTextFields
            )
          )
        )
      )
      .join(',')
  );
  return [headerRow, ...dataRows].join('\n');
}

/** Builds CSV from the supplied rows and triggers a browser download. */
export const downloadAll = (columns: ExportColumn[], mergedRows: any[]): void => {
  try {
    if (mergedRows.length === 0) {
      showBulkToast(
        BOND_BIDDING_TOAST_TITLE.noDataToExport,
        BOND_BIDDING_MESSAGES.download.noRowsMatchFilters,
        'warning'
      );
      return;
    }
    const timeFields = [...BOND_BIDDING_TIME_FIELDS];
    const dateFields = [...BOND_BIDDING_DATE_FIELDS];
    const numericFields = [...BOND_BIDDING_NUMERIC_FIELDS];
    const preserveAsTextFields = new Set(BOND_BIDDING_PRESERVE_AS_TEXT_FIELDS);
    const csv = buildCsv(mergedRows, columns, timeFields, dateFields, numericFields, preserveAsTextFields);
    triggerCsvDownload(csv);
    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.exportComplete,
      BOND_BIDDING_MESSAGES.download.exportedRows(mergedRows.length),
      'success'
    );
  } catch (error) {
    showBulkToast(
      BOND_BIDDING_TOAST_TITLE.exportFailed,
      (error as Error)?.message || BOND_BIDDING_MESSAGES.download.exportFailedFallback,
      'error',
      { closeTimeout: 5000 }
    );
  }
}